import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { DepositsService, DepositWithFolderAndStandard, Layer } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class StandardizedLayerService {
    private readonly depositsById = new Map<string, DepositWithFolderAndStandard>();
    private readonly depositsService = inject(DepositsService);

    public async getStandardizedLayerIds(layers: Pick<Layer, 'deposit' | 'id'>[]): Promise<Set<string>> {
        const layerIdsByDepositId = this.getLayerIdsByDepositId(layers);

        if (!layerIdsByDepositId.size) {
            return new Set<string>();
        }

        const standardizedLayerIds = new Set<string>();

        await Promise.all(
            Array.from(layerIdsByDepositId.entries()).map(
                async ([depositId, layerIds]: [string, string[]]): Promise<void> => {
                    const deposit = await this.getDepositWithStandard(depositId);
                    if (!deposit?.standard) {
                        return;
                    }

                    layerIds.forEach((layerId: string) => {
                        standardizedLayerIds.add(layerId);
                    });
                },
            ),
        );

        return standardizedLayerIds;
    }

    private async getDepositWithStandard(depositId: string): Promise<DepositWithFolderAndStandard | null> {
        const existingDeposit = this.depositsById.get(depositId);
        if (existingDeposit) {
            return existingDeposit;
        }

        try {
            const deposit = await firstValueFrom(
                this.depositsService.depositControllerGetDepositWithFolderAndStandard({
                    id: depositId,
                }),
            );

            this.depositsById.set(depositId, deposit);

            return deposit;
        } catch {
            return null;
        }
    }

    private getLayerIdsByDepositId(layers: Pick<Layer, 'deposit' | 'id'>[]): Map<string, string[]> {
        const layerIdsByDepositId = new Map<string, string[]>();

        for (const layer of layers) {
            if (!layer.deposit) {
                continue;
            }

            const layerIds = layerIdsByDepositId.get(layer.deposit) ?? [];

            layerIds.push(layer.id);
            layerIdsByDepositId.set(layer.deposit, layerIds);
        }

        return layerIdsByDepositId;
    }
}
